'use client';

import { useMemo } from 'react';

import type {
  ComboboxGroupType,
  ComboboxOptionType,
  ComboboxOptionsType,
} from '../types/combobox-root.types';
import { isGroup } from '../utils/is-group';

/**
 * Custom hook that filters combobox options based on user input
 *
 * This hook efficiently filters options by:
 * - Returning all options if the input is not dirty or empty
 * - Filtering string options by direct string matching
 * - Filtering object options by matching against label or meta properties
 * - Preserving group structure while filtering their nested options
 *
 * @param params - Parameters for the hook
 * @param params.isDirty - Whether the input has been modified by the user
 * @param params.optionFilter - User provider option matcher override
 * @param params.options - The complete list of options to filter
 * @param params.value - The current input value to filter against
 * @returns Filtered options that match the input value
 */
export const useComboboxFilteredOptions = ({
  autoCompleteBehavior,
  isDirty,
  optionFilter,
  options,
  value,
}: {
  autoCompleteBehavior: 'none' | 'manual' | 'automatic';
  isDirty: boolean;
  optionFilter?: (inputValue: string, text: string) => boolean;
  options: ComboboxOptionsType;
  value: string;
}) =>
  useMemo(() => {
    // Skip filtering if the input hasn't been modified or is empty
    if (!isDirty || !value || autoCompleteBehavior === 'none') {
      return options;
    }

    // Convert input to lowercase once for case-insensitive matching
    const lowerCaseInput = value.toLowerCase();

    /**
     * Checks if a text contains the input value
     *
     * @param text - The text to check against the input
     * @returns True if the text includes the input value, false otherwise
     */
    const matchesInput = (text: string | undefined): boolean => {
      if (optionFilter) {
        return text ? optionFilter(lowerCaseInput, text.toLowerCase()) : false;
      }

      return text ? text.toLowerCase().includes(lowerCaseInput) : false;
    };

    /**
     * Determines if an option matches the filter criteria
     *
     * @param option - The option to check
     * @returns Boolean indicating whether the option matches
     */
    const matchesFilter = (option: ComboboxOptionType): boolean => {
      // Handle string options
      if (typeof option === 'string') {
        return matchesInput(option);
      }
      // Handle object options (match against label or meta)
      return matchesInput(option.label) || matchesInput(option.meta);
    };

    /**
     * Filters options within a group
     *
     * @param group - The group containing options to filter
     * @returns The original group (if all options match), a new group with filtered options, or null if no options match
     */
    const filterGroup = (group: ComboboxGroupType): ComboboxGroupType | null => {
      // Filter the options within the group
      const filteredGroupOptions = group.options.filter(matchesFilter);

      // Optimization: return different results based on filtering outcomes
      return filteredGroupOptions.length === 0
        ? null // No options match, exclude the entire group
        : filteredGroupOptions.length === group.options.length
          ? group // All options match, preserve the original group object
          : { ...group, options: filteredGroupOptions }; // Some options match, create new group with filtered options
    };

    /**
     * Processes an option or group for the final filtering
     */
    const processOption = (
      option: ComboboxOptionType | ComboboxGroupType,
    ): ComboboxOptionType | ComboboxGroupType | null => {
      return isGroup(option) ? filterGroup(option) : matchesFilter(option) ? option : null;
    };

    // Process all options, handling both groups and individual options
    return options
      .map(processOption)
      .filter(
        (option): option is ComboboxOptionType | ComboboxGroupType => option !== null,
      ) as ComboboxOptionsType;
  }, [isDirty, options, value, optionFilter, autoCompleteBehavior]);
